// src/components/MediaDisplay.js
import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { imageMap } from '../utils/imageImports';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const zoomIn = keyframes`
  from {
    transform: scale(1);
  }
  to {
    transform: scale(1.05);
  }
`;

// Wrapper for the entire media component
const MediaWrapper = styled.div`
  position: relative;
  /* Every figure sits in a centered frame at its natural size, capped by the reading
     column; nothing stretches to fill. Grid cells keep filling their own cell. */
  width: ${({ $inGrid }) => ($inGrid ? '100%' : 'fit-content')};
  max-width: 100%;
  margin: ${({ theme, $inGrid }) => ($inGrid ? '0' : `${theme.spacing['2xl']} auto`)};
  animation: ${fadeIn} 0.6s ${({ theme }) => theme.transitions.easeOut};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin: ${({ theme, $inGrid }) => $inGrid ? '0' : `${theme.spacing.xl} auto`};
  }
`;

// Container with glassmorphism effect (or solid for grids)
const MediaContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 0;
  backdrop-filter: ${({ $inGrid }) => $inGrid ? 'none' : 'blur(10px) saturate(180%)'};
  -webkit-backdrop-filter: ${({ $inGrid }) => $inGrid ? 'none' : 'blur(10px) saturate(180%)'};
  background: ${({ theme, $inGrid }) => $inGrid ? theme.colors.surface : theme.colors.glassSurface};
  border: 1px solid ${({ theme }) => theme.colors.borderSubtle};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  transition: all ${({ theme }) => theme.transitions.base};
  
  /* Subtle gradient overlay - only for non-grid */
  &::before {
    content: ${({ $inGrid }) => $inGrid ? 'none' : '""'};
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.colors.primary}05 0%,
      transparent 100%
    );
    pointer-events: none;
    z-index: 1;
  }
  
  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.xl};
    transform: ${({ $inGrid }) => $inGrid ? 'none' : 'translateY(-2px)'};
    
    &::before {
      background: ${({ $inGrid, theme }) => $inGrid ? 'none' : `linear-gradient(
        135deg,
        ${theme.colors.primary}10 0%,
        transparent 100%
      )`};
    }
  }
`;

// Caption styling
const Caption = styled.p`
  margin-top: ${({ theme, $inGrid }) => $inGrid ? '0' : theme.spacing.sm};
  padding: ${({ theme, $inGrid }) => $inGrid 
    ? `${theme.spacing.base} ${theme.spacing.base}` 
    : `0 ${theme.spacing.base}`};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-style: ${({ $inGrid }) => $inGrid ? 'normal' : 'italic'};
  line-height: 1.5;
  background: ${({ theme, $inGrid }) => $inGrid ? theme.colors.surface : 'transparent'};
  border-top: ${({ theme, $inGrid }) => $inGrid ? `1px solid ${theme.colors.borderLight}` : 'none'};
`;

// Loading skeleton
const LoadingSkeleton = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.surface} 0%,
    ${({ theme }) => theme.colors.background} 50%,
    ${({ theme }) => theme.colors.surface} 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 0;
  z-index: 2;
`;

// Error state
const ErrorState = styled.div`
  padding: ${({ theme }) => theme.spacing['2xl']};
  text-align: center;
  color: ${({ theme }) => theme.colors.textTertiary};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 0;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};

  svg {
    width: 48px;
    height: 48px;
    margin: 0 auto ${({ theme }) => theme.spacing.base};
    opacity: 0.5;
  }
  
  p {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

// Enhanced video player
const VideoPlayer = styled.video`
  width: 100%;
  height: auto;
  display: block;
  border-radius: 0;
  background: ${({ theme }) => theme.colors.surface};
  position: relative;
  z-index: 2;
  transition: transform ${({ theme }) => theme.transitions.base};
  
  /* Custom controls styling */
  &::-webkit-media-controls-panel {
    background: linear-gradient(
      to bottom,
      transparent,
      rgba(26, 47, 42, 0.7)
    );
  }
  
  &::-webkit-media-controls-play-button {
    background-color: ${({ theme }) => theme.colors.secondary};
    border-radius: 50%;
  }
  
  &:focus {
    outline: 3px solid ${({ theme }) => theme.colors.focusRing};
    outline-offset: 2px;
  }
`;

// Play button overlay for video
const PlayOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(26, 47, 42, 0.4);
  backdrop-filter: blur(4px);
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  z-index: 3;
  
  &:hover {
    background: rgba(26, 47, 42, 0.2);
    
    svg {
      transform: scale(1.1);
    }
  }
  
  svg {
    width: 64px;
    height: 64px;
    color: ${({ theme }) => theme.colors.background};
    filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
    transition: transform ${({ theme }) => theme.transitions.fast};
  }
`;

// Enhanced image with loading states
const StyledImage = styled.img`
  /* Natural size, capped by the column: small screenshots never upscale into blur.
     Grid cells still fill uniformly. */
  width: ${({ $inGrid }) => ($inGrid ? '100%' : 'auto')};
  max-width: 100%;
  height: auto;
  display: block;
  border-radius: 0;
  position: relative;
  z-index: 2;
  transition: all ${({ theme }) => theme.transitions.base};
  opacity: ${({ $loaded }) => $loaded ? 1 : 0};
  animation: ${({ $loaded }) => $loaded ? fadeIn : 'none'} 0.6s ${({ theme }) => theme.transitions.easeOut};
  
  &:hover {
    cursor: ${({ $zoomable }) => $zoomable ? 'zoom-in' : 'default'};
  }
`;

// Lightbox for full-screen image view
const Lightbox = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  cursor: zoom-out;
  animation: ${fadeIn} 0.3s ease-out;
  
  img {
    max-width: 90vw;
    max-height: 90vh;
    object-fit: contain;
    border-radius: ${({ theme }) => theme.borderRadius.base};
    animation: ${zoomIn} 0.3s ${({ theme }) => theme.transitions.easeOut};
  }
  
  /* Close button */
  button {
    position: absolute;
    top: ${({ theme }) => theme.spacing.xl};
    right: ${({ theme }) => theme.spacing.xl};
    background: ${({ theme }) => theme.colors.surface};
    border: none;
    width: 48px;
    height: 48px;
    border-radius: ${({ theme }) => theme.borderRadius.full};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all ${({ theme }) => theme.transitions.fast};
    box-shadow: ${({ theme }) => theme.shadows.lg};
    
    &:hover {
      transform: scale(1.1);
      background: ${({ theme }) => theme.colors.background};
    }
    
    &:focus {
      outline: 3px solid ${({ theme }) => theme.colors.focusRing};
      outline-offset: 2px;
    }
    
    svg {
      width: 24px;
      height: 24px;
      color: ${({ theme }) => theme.colors.text};
    }
  }
`;

const MediaDisplay = ({
  url,
  alt,
  type = 'image', // Default to image type
  caption,
  zoomable = true,
  poster,
  autoplay = false,
  loop = false,
  muted = true,
  fullWidth = true,
  inGrid = false,  // New prop to handle grid context
  size = null,  // Optional size prop (e.g., "75%", "50%")
  onImageClick = null  // Callback for parent-managed lightbox
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [showPlayOverlay, setShowPlayOverlay] = useState(!autoplay);
  const videoRef = useRef(null);
  
  // Parse URL if it contains semicolons (take first item only)
  const cleanUrl = url && typeof url === 'string' 
    ? url.split(';')[0].trim() 
    : url;
  
  // Parse caption if it contains semicolons (take first item only)
  const cleanCaption = caption && typeof caption === 'string' 
    ? caption.split(';')[0].trim() 
    : caption;
  
  // Parse alt if it contains semicolons (take first item only)
  const cleanAlt = alt && typeof alt === 'string' 
    ? alt.split(';')[0].trim() 
    : alt;
  
  // Handle autoplay for videos
  useEffect(() => {
    if (type === 'video' && autoplay && videoRef.current && cleanUrl) {
      videoRef.current.play().catch(error => {
        console.warn('Autoplay failed:', error);
        setShowPlayOverlay(true);
      });
    }
  }, [type, autoplay, cleanUrl]);
  
  // Early return if no URL
  if (!cleanUrl) {
    console.warn('MediaDisplay: No URL provided');
    return null;
  }
  
  // Get the mapped media path
  const mediaSrc = imageMap[cleanUrl] || cleanUrl;
  const posterSrc = poster ? (imageMap[poster] || poster) : null;
  
  // Determine media type from file extension if not specified
  const inferredType = type || (cleanUrl.match(/\.(mp4|webm|ogg)$/i) ? 'video' : 'image');
  
  // Handle image load
  const handleImageLoad = () => {
    setIsLoading(false);
  };
  
  // Handle errors
  const handleError = (e) => {
    console.warn(`Failed to load media: ${cleanUrl}`, e);
    setHasError(true);
    setIsLoading(false);
  };
  
  // Handle lightbox - delegate to parent if callback provided
  const handleImageClick = () => {
    if (zoomable && inferredType !== 'video') {
      if (onImageClick) {
        onImageClick();
      } else if (!inGrid) {
        setIsLightboxOpen(true);
      }
    }
  };
  
  const closeLightbox = (e) => {
    e.stopPropagation();
    setIsLightboxOpen(false);
  };
  
  // Handle video play
  const handleVideoPlay = () => {
    if (videoRef.current) {
      videoRef.current.play().then(() => {
        setShowPlayOverlay(false);
      }).catch(error => {
        console.warn('Video play failed:', error);
      });
    }
  };
  
  // Reset play overlay when video ends (if not looping)
  const handleVideoEnded = () => {
    if (!loop) {
      setShowPlayOverlay(true);
    }
  };
  
  // Error state
  if (hasError) {
    return (
      <MediaWrapper $fullWidth={fullWidth} $inGrid={inGrid} $size={size}>
        <ErrorState>
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
            />
          </svg>
          <p>Unable to load media</p>
          <small>{cleanUrl}</small>
        </ErrorState>
      </MediaWrapper>
    );
  }
  
  // Render based on media type
  if (inferredType === 'video') {
    return (
      <MediaWrapper $fullWidth={fullWidth} $inGrid={inGrid} $size={size}>
        <MediaContainer $inGrid={inGrid}>
          {isLoading && <LoadingSkeleton />}
          <VideoPlayer 
            ref={videoRef}
            controls
            autoPlay={autoplay}
            loop={loop}
            muted={muted}
            playsInline
            onLoadedData={() => setIsLoading(false)}
            onError={handleError}
            onEnded={handleVideoEnded}
            poster={posterSrc}
            preload="metadata"
          >
            <source src={mediaSrc} type="video/mp4" />
            <source src={mediaSrc.replace('.mp4', '.webm')} type="video/webm" />
            <source src={mediaSrc.replace('.mp4', '.ogg')} type="video/ogg" />
            Your browser does not support the video tag.
          </VideoPlayer>
          {showPlayOverlay && !isLoading && !autoplay && (
            <PlayOverlay onClick={handleVideoPlay}>
              <svg fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </PlayOverlay>
          )}
        </MediaContainer>
        {cleanCaption && <Caption $inGrid={inGrid}>{cleanCaption}</Caption>}
      </MediaWrapper>
    );
  }
  
  // Default to image (includes gif)
  return (
    <MediaWrapper $fullWidth={fullWidth} $inGrid={inGrid} $size={size}>
      <MediaContainer onClick={handleImageClick} $inGrid={inGrid}>
        {isLoading && <LoadingSkeleton />}
        <StyledImage
          src={mediaSrc}
          alt={cleanAlt || cleanCaption || 'Image'}
          onLoad={handleImageLoad}
          onError={handleError}
          $loaded={!isLoading}
          $inGrid={inGrid}
          $zoomable={zoomable && (!inGrid || !!onImageClick)}
          loading="lazy"
        />
      </MediaContainer>
      {cleanCaption && <Caption $inGrid={inGrid}>{cleanCaption}</Caption>}
      
      {/* Lightbox for full-screen view - disabled in grid context */}
      {isLightboxOpen && !inGrid && (
        <Lightbox onClick={closeLightbox}>
          <img src={mediaSrc} alt={cleanAlt || cleanCaption || 'Image'} />
          <button onClick={closeLightbox} aria-label="Close lightbox">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M6 18L18 6M6 6l12 12" 
              />
            </svg>
          </button>
        </Lightbox>
      )}
    </MediaWrapper>
  );
};

export default MediaDisplay;